import api from "app/api";
import { Permissions } from "app/constants";
import {
  UseFormReturnType,
  Validator,
  enumToOptions,
  globalMessageHandler,
  useForm,
} from "common";
import useDictionaries from "features/dictionaries/useDictionaries";
import { departureCapacityRules } from "features/event-groups/departureCapacityRules";
import {
  saveWithCapacityWarning,
  validateBatchEventCapacity,
} from "features/events/capacityValidationApi";
import { OptionItem } from "features/options/optionApi";
import useAccess from "features/profile/useAccess";
import { ProgramVariantItem } from "features/program-variants/programVariantApi";
import { ProgramItem, availablePrograms } from "features/programs/programApi";
import {
  ScheduleLimit,
  ScheduleOption,
  ScheduleSeatingLayout,
  ScheduleVariant,
} from "features/schedules/scheduleApi";
import { LIMIT_TYPES } from "features/schedules/scheduleConstants";
import { generateTimeOptions } from "helpers/dateHelper";
import { validateEvent } from "helpers/validationHelper";
import I18n from "i18n";
import { useMemo, useRef } from "react";

export type GroupEventDay = {
  date: string;
  start_times: string[];
};

export type GroupEventFormState = {
  boat_id?: number;
  duration?: number;
  number_of_places?: number;
  number_of_online_places?: number;
  places_tolerance?: number;
  program_ids: number[];
  limits: ScheduleLimit[];
  variants: ScheduleVariant[];
  options: ScheduleOption[];
  seating_layouts: ScheduleSeatingLayout[];
  days: GroupEventDay[];
};

export interface UseGroupEventCreateFormReturnType extends UseFormReturnType<GroupEventFormState> {
  formOptions: any;
  programs: any[];
  hasOptions: boolean;
  times: string[];
  type: any[];
  reset: () => void;
}

export default function useGroupEventCreateForm(
  afterSaveCallback?: () => void,
): UseGroupEventCreateFormReturnType {
  const { dictionaries, filterOptions, resolveOptions } = useDictionaries();
  const { hasPermissions } = useAccess();
  const times = useMemo(() => generateTimeOptions(30), []);

  // Keep a fresh ref to the reset function so afterSave can call it
  const resetRef = useRef<() => void>();

  const form = useForm<GroupEventFormState>({
    translationCategory: "Event",
    ignoreNavigateAfterCreate: true,
    ignoreSuccessNotification: true,
    rules: {
      boat_id: Validator.rule.any().required(),
      duration: Validator.rule.any().required(),
      ...departureCapacityRules(),
      program_ids: Validator.rule.any().required(),
      variants: Validator.rule.any().required(),
      limits: Validator.rule.any().required(),
    },
    transformChange(newItem, id) {
      if (id === "days") {
        let newDays = [...(newItem.days || [])];
        const copyIdx = newDays.findIndex((d: any) => d._copy === true);
        if (copyIdx !== -1) {
          if (copyIdx < newDays.length - 1) {
            newDays[copyIdx + 1] = {
              ...newDays[copyIdx + 1],
              start_times: newDays[copyIdx].start_times,
            };
          }
          const { _copy, ...rest } = newDays[copyIdx] as any;
          newDays[copyIdx] = rest;
        }
        return { ...newItem, days: newDays };
      }
      return newItem;
    },
    onValidationFailed: () => {
      globalMessageHandler.snack({
        severity: "error",
        message: I18n.t("App.formHasErrors"),
      });
    },
    customValidation(newItem) {
      const customErrors: any = {};

      if (!newItem.days?.length) {
        customErrors.days = I18n.t("Event.groupCreate.daysRequired");
      } else if (newItem.days.length > 10) {
        customErrors.days = I18n.t("Event.groupCreate.maxDaysExceeded");
      }

      const result = validateEvent(
        newItem.limits,
        newItem.variants,
        newItem.options,
        programs,
        programVariants,
        hasOptions,
      );
      if (result.limits) customErrors.limits = result.limits;
      if (result.variants) customErrors.variants = result.variants;
      if (result.options) customErrors.options = result.options;

      return Object.keys(customErrors).length > 0 ? customErrors : undefined;
    },
    createItem: async item => {
      const save = () => api.post("events/batch-create", item);
      const { success, data, error } = hasPermissions(
        Permissions.BoatCapacityOverflow,
      )
        ? await saveWithCapacityWarning(
            () => validateBatchEventCapacity(item),
            save,
          )
        : await save();
      if (success) {
        globalMessageHandler.snack({
          severity: "success",
          message: I18n.t("Event.groupCreate.successMessage", {
            count: data?.created_count ?? 0,
          }),
        });
      }
      return { success, data, error };
    },
    afterSave: () => {
      resetRef.current?.();
      afterSaveCallback?.();
    },
  });

  const { formOptions, programs, programVariants, hasOptions, type } =
    useMemo(() => {
      const programs = resolveOptions<ProgramItem>(
        "programs",
        form.item.program_ids,
      );
      const programVariants = filterOptions<ProgramVariantItem>("variants", v =>
        form.item.program_ids?.includes(v.program_id),
      );
      const opts = filterOptions<OptionItem>("options", o =>
        form.item.program_ids?.includes(o.program_id),
      ).map(o => ({ ...o, label: `${o.option_group?.name}: ${o.name}` }));

      return {
        formOptions: {
          boat_id: dictionaries.boats,
          program_ids: availablePrograms(dictionaries.programs),
          variants: programVariants,
          options: opts,
          layouts: dictionaries.layouts,
        },
        programs,
        programVariants,
        hasOptions: programs?.some(p => p.has_options) ?? false,
        type: enumToOptions(LIMIT_TYPES, "Limit.options.type"),
      };
    }, [form.item.program_ids, dictionaries]);

  function reset() {
    form.setItem({} as GroupEventFormState);
    form.setErrors({});
  }

  // Keep resetRef current so afterSave always calls the latest reset
  resetRef.current = reset;

  console.log({ formErrors: form.errors, program_ids: form.item.program_ids });

  return {
    ...form,
    formOptions,
    programs,
    hasOptions,
    times,
    type,
    reset,
  };
}
